import { createFeatureSelector, createSelector } from '@ngrx/store';

import {
  isErrorState,
  isLoadingState,
  isResultState,
  selectResultState,
} from '../../@core/fetch-state.utils';

import { OrderClient } from './orders.model';
import { OrdersState } from './orders.state.interface';

export const selectOrdersState = createFeatureSelector<OrdersState>('orders');

export const selectOrderDetails = createSelector(selectOrdersState, (state) =>
  selectResultState(state.order),
);
export const selectOrderLoading = createSelector(selectOrdersState, (state) =>
  isLoadingState(state.order),
);
export const selectOrderError = createSelector(selectOrdersState, (state) =>
  isErrorState(state.order),
);

export const selectUpdatedOrder = createSelector(selectOrdersState, (state) =>
  selectResultState(state.updatedOrder),
);

export const selectUpdatedOrderLoading = createSelector(selectOrdersState, (state) =>
  isLoadingState(state.updatedOrder),
);

export const selectUpdatedOrderError = createSelector(selectOrdersState, (state) =>
  isErrorState(state.updatedOrder),
);

export const selectOrderAssignees = createSelector(selectOrdersState, (state) =>
  selectResultState(state.orderAssignees),
);

export const selectOrderStatuses = createSelector(selectOrdersState, (state) =>
  selectResultState(state.orderStatuses),
);

export const selectOrderClients = createSelector(
  selectOrdersState,
  (state): OrderClient[] | undefined => selectResultState(state.orderClients),
);

export const selectAddNoteLoading = createSelector(
  selectOrdersState,
  (state) => state.addNoteLoading,
);

export const selectOrderMaterialsCategories = createSelector(
  selectOrdersState,
  (state: OrdersState) => state.materialsCategories,
);

export const selectOrderMaterialsStatus = createSelector(
  selectOrdersState,
  (state: OrdersState) => state.materialsStatus,
);

export const selectPackingSlipCategories = createSelector(
  selectOrdersState,
  (state: OrdersState) => state.packingSlipCategories,
);

export const selectPackingSlipCategoriesLoading = createSelector(
  selectOrdersState,
  (state: OrdersState) => state.packingSlipCategoriesLoading,
);

export const selectPackingSlipCategoriesError = createSelector(
  selectOrdersState,
  (state: OrdersState) => state.packingSlipCategoriesError,
);

export const selectOrderSaving = createSelector(selectOrdersState, (state) => state.loading);

// Select all orders (for table)
export const selectAllOrders = createSelector(selectOrdersState, (state) =>
  selectResultState(state.ordersList),
);

export const selectOrdersLoading = createSelector(selectOrdersState, (state) => state.loading);

export const selectOrdersError = createSelector(selectOrdersState, (state) => state.error);

export const selectOrdersTotalCount = createSelector(
  selectOrdersState,
  (state) => state.totalCount,
);

export const selectOrdersList = createSelector(selectOrdersState, (state) =>
  state.myOrdersList ? selectResultState(state.myOrdersList) : undefined,
);

export const selectOrdersListLoading = createSelector(selectOrdersState, (state) =>
  state.myOrdersList ? isLoadingState(state.myOrdersList) : false,
);

export const selectOrdersListError = createSelector(selectOrdersState, (state) =>
  state.myOrdersList ? isErrorState(state.myOrdersList) : false,
);

export const selectOrdersListLength = createSelector(selectOrdersState, (state) =>
  state.myOrdersListLength ? selectResultState(state.myOrdersListLength) : undefined,
);

export const selectMyOrdersTotalPrice = createSelector(selectOrdersState, (state) =>
  state.myOrdersTotalPrice ? selectResultState(state.myOrdersTotalPrice) : undefined,
);

export const selectPastDueOrders = createSelector(selectOrdersState, (state) =>
  state.pastDueOrders ? selectResultState(state.pastDueOrders) : undefined,
);

export const selectPastDueOrdersLoading = createSelector(selectOrdersState, (state) =>
  state.pastDueOrders ? isLoadingState(state.pastDueOrders) : false,
);

export const selectPastDueOrdersError = createSelector(selectOrdersState, (state) =>
  state.pastDueOrders ? isErrorState(state.pastDueOrders) : false,
);

export const selectPastDueOrdersLength = createSelector(selectOrdersState, (state) =>
  state.pastDueOrdersLength ? selectResultState(state.pastDueOrdersLength) : undefined,
);

export const selectPastDueOrdersTotalPrice = createSelector(selectOrdersState, (state) =>
  state.pastDueOrdersTotalPrice ? selectResultState(state.pastDueOrdersTotalPrice) : undefined,
);

export const selectOrdersDueToday = createSelector(selectOrdersState, (state) =>
  state.ordersDueToday ? selectResultState(state.ordersDueToday) : undefined,
);

export const selectOrdersDueTodayLoading = createSelector(selectOrdersState, (state) =>
  state.ordersDueToday ? isLoadingState(state.ordersDueToday) : false,
);

export const selectOrdersDueTodayError = createSelector(selectOrdersState, (state) =>
  state.ordersDueToday ? isErrorState(state.ordersDueToday) : false,
);

export const selectOrdersDueTodayLength = createSelector(selectOrdersState, (state) =>
  state.ordersDueTodayLength ? selectResultState(state.ordersDueTodayLength) : undefined,
);

export const selectOrdersDueTodayTotalPrice = createSelector(selectOrdersState, (state) =>
  state.ordersDueTodayTotalPrice ? selectResultState(state.ordersDueTodayTotalPrice) : undefined,
);

export const selectLists = createSelector(selectOrdersState, (state) => state.lists);

export const selectListsTotalCount = createSelector(
  selectOrdersState,
  (state) => state.listsTotalCount,
);

export const selectListsLoading = createSelector(selectOrdersState, (state) => state.listsLoading);

export const selectListsError = createSelector(selectOrdersState, (state) => state.listsError);

export const selectCustomerCodes = createSelector(
  selectOrdersState,
  (state) => selectResultState(state.customerCodes) || [],
);

export const selectSingleList = createSelector(selectOrdersState, (state) => state.singleList);
export const selectSingleListResults = createSelector(
  selectOrdersState,
  (state) => state.singleList?.results,
);

export const selectSingleListLoading = createSelector(
  selectOrdersState,
  (state) => state.singleListLoading,
);

export const selectSingleListError = createSelector(
  selectOrdersState,
  (state) => state.singleListError,
);

export const selectUpdateSingleListLoading = createSelector(
  selectOrdersState,
  (state) => state.singleListLoading,
);

export const selectUpdateSingleListError = createSelector(
  selectOrdersState,
  (state) => state.singleListError,
);

export const selectExportDetails = createSelector(
  selectOrdersState,
  (state) => state.exportDetails,
);
export const selectExportDetailsLoading = createSelector(
  selectOrdersState,
  (state) => state.exportDetailsLoading,
);
export const selectExportDetailsError = createSelector(
  selectOrdersState,
  (state) => state.exportDetailsError,
);

export const selectExportOrderLoading = createSelector(
  selectOrdersState,
  (state) => state.exportOrderLoading,
);
export const selectExportOrderError = createSelector(
  selectOrdersState,
  (state) => state.exportOrderError,
);

export const selectExportOrderSuccessResponse = createSelector(
  selectOrdersState,
  (state) => state.exportOrderSuccessResponse,
);

export const selectOrdersPriceTotal = createSelector(
  selectOrdersState,
  (state) => state.priceTotal,
);
export const selectOrdersPriceTotalNotShipped = createSelector(
  selectOrdersState,
  (state) => state.priceTotalNotShipped,
);

export const selectExportOrdersBundleLoading = createSelector(
  selectOrdersState,
  (state) => state.exportOrdersBundleLoading,
);
export const selectExportOrdersBundleError = createSelector(
  selectOrdersState,
  (state) => state.exportOrdersBundleError,
);
export const selectExportOrdersBundleResponse = createSelector(
  selectOrdersState,
  (state) => state.exportOrdersBundleResponse,
);

export const selectCreatedOrder = createSelector(selectOrdersState, (state) =>
  selectResultState(state.createdOrder),
);

export const selectCreatedOrderSuccess = createSelector(selectOrdersState, (state) =>
  isResultState(state.createdOrder),
);

export const selectCreatedOrderError = createSelector(selectOrdersState, (state) =>
  isErrorState(state.createdOrder),
);

export const selectDeleteList = createSelector(selectOrdersState, (state) => state.deleteList);
export const selectDeleteListLoading = createSelector(selectOrdersState, (state) =>
  isLoadingState(state.deleteList),
);
export const selectDeleteListError = createSelector(selectOrdersState, (state) =>
  isErrorState(state.deleteList),
);
export const selectDeleteListSuccess = createSelector(selectOrdersState, (state) =>
  isResultState(state.deleteList),
);
